import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ProgressBar } from '../../../../components/ui/ProgressBar';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { QueueList } from '../../components/QueueList';
import { TaskCard } from '../../components/TaskCard';
import { TaskMeta } from '../../components/TaskMeta';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW } from '../../../../features/operator/operatorConstants';
import { fetchReceivingQueue, findReceipt } from '../../../../features/operatorReceiving/operatorReceivingThunks';
import { formatQuantity, receiptStatusLabel } from '../../../../utils/inventoryLabels';

/** Recepción: the open receipts, in the tray's order. Scanning a guide opens its receipt. */
export const ReceivingQueue = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { queue } = useSelector((state) => state.operatorReceiving);

  useEffect(() => {
    dispatch(taskEntered({ flow: OPERATOR_FLOW.RECEIVING }));
    dispatch(fetchReceivingQueue({ page: 1 }));
  }, [dispatch]);

  // Opens what was found only while the queue is on screen: meanwhile the operator may have opened another.
  const openScanned = async (code, source) => {
    const action = await dispatch(findReceipt({ code, source }));
    if (findReceipt.fulfilled.match(action) && !action.meta.offScreenTask) {
      navigate(`/receiving/${action.payload.receipt_id}`);
    }
  };

  return (
    <div className="space-y-5">
      <OperatorScanPanel
        prompt="Escanea la guía de la recepción"
        hint="O toca una de la lista."
        accepting={!queue.finding}
        lastCode={queue.lastCode}
        holdReads={false}
        busyLabel="Buscando…"
        onCode={openScanned}
      />

      <QueueList
        queue={queue}
        testId="receipt-list"
        emptyText="No hay recepciones abiertas."
        onRetry={() => dispatch(fetchReceivingQueue({ page: 1 }))}
        onMore={() => dispatch(fetchReceivingQueue({ page: queue.page + 1 }))}
        onRefresh={() => dispatch(fetchReceivingQueue({ page: 1 }))}
        refreshThunk={fetchReceivingQueue}
      >
        {queue.items.map((receipt) => (
          <li key={receipt.id}>
            <TaskCard
              to={`/receiving/${receipt.id}`}
              testId="receipt-card"
              data={{ 'data-receipt-id': receipt.id }}
              title={receipt.code}
              subtitle={[receipt.company_name, receipt.supplier, receipt.reference].filter(Boolean).join(' · ')}
              aside={
                <span className="rounded-full border border-border px-2.5 py-0.5 text-xs font-semibold text-text-secondary">
                  {receiptStatusLabel(receipt.status)}
                </span>
              }
            >
              <div className="space-y-3">
                <ProgressBar
                  size="sm"
                  value={Number(receipt.received_units) || 0}
                  max={Number(receipt.expected_units) || 0}
                  label={`${formatQuantity(receipt.received_units)} de ${formatQuantity(receipt.expected_units)} un`}
                />
                <TaskMeta task={receipt} />
              </div>
            </TaskCard>
          </li>
        ))}
      </QueueList>
    </div>
  );
};
